/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { GetWineFilterable } from './get-wine-filterable-class';
import { WineMapper } from './get-wine-mapper-class';

/**
 * GetWineInternationalLocation Input Action
 */

export class GetWineInternationalLocationInput extends GetWineFilterable<IGetWineInternationalQueryFilters> {

    private readonly _url: string = 'https://api.smwe.com';

    public get url(): string {
        const { brand_code, country } = this.queryFilters;

        const fullUrl = `${this._url}/v1/${brand_code}/distributors?country=${country}`;
        if (!brand_code) {
            console.error('Brand Code is not declared. This is required for the module to do calls');
        }
        return fullUrl;
    }
}

// =============================================================================
// MAPPER
// =============================================================================

enum GetWineInternationalLocationMap {
    totalResults = 'total_results',
    totalPages = 'total_pages',
    page = 'page'
}

enum GetWineInternationalLocationDistributorMap {
    jdeDistributorNumber = 'jde_distributor_number',
    brandCode = 'brand_code',
    name = 'name',
    city = 'city',
    state = 'state',
    zip = 'zip',
    phone = 'phone',
    url = 'url'
}

enum GetWineInternationalLocationCountryMap {
    iso = 'iso',
    name = 'name',
    printableName = 'printable_name',
    iso3 = 'iso3',
    numCode = 'numcode'
}

/**
 * Class definition for the output controller of the API. Acts as a middle layer
 * and ensures that the data stays consistent internally
 */
class GetWineLocationInternationalMapper extends WineMapper<IGetWineInternationalLocationInput> {
    public get output(): IGetWineInternationalLocationOutput {
        const output: IGetWineInternationalLocationOutput = {
            data: [],
            totalPages: this._input[GetWineInternationalLocationMap.totalPages],
            totalResults: this._input[GetWineInternationalLocationMap.totalResults],
            page: this._input[GetWineInternationalLocationMap.page]
        };

        this._input.distributors.forEach(distributor => {
            const distributorOutput =
                this._applyMap<IGetWineInternationalDistributorOuput, IGetWineInternationalDistributorInput, typeof GetWineInternationalLocationDistributorMap>(distributor, GetWineInternationalLocationDistributorMap);
            distributorOutput.country =
                this._applyMap<IGetWineInternationalDistributorCountryOutput, IGetWineInternationalDistributorCountryInput, typeof GetWineInternationalLocationCountryMap>(distributor.country, GetWineInternationalLocationCountryMap);
            output.data.push(distributorOutput);
        });
        return output;
    }
}

// =============================================================================
// INTERFACES
// =============================================================================

export interface IGetWineInternationalQueryFilters {
    brand_code?: string;
    country: string;
    pagination?: boolean;
    page?: number;
    per_page?: number;
}

// =============================================================================
// OUTPUT INTERFACES
// =============================================================================

export interface IGetWineInternationalLocationOutput {
    totalResults: number;
    totalPages: number;
    page: number;
    data: IGetWineInternationalDistributorOuput[];
}

export interface IGetWineInternationalDistributorOuput {
    jdeDistributorNumber: number;
    brandCode: string;
    name: string;
    city: string;
    state: string;
    country: IGetWineInternationalDistributorCountryOutput;
    zip: string;
    phone: string;
    url: string;
}

interface IGetWineInternationalDistributorCountryOutput {
    iso: string;
    name: string;
    printableName: string;
    iso3: string;
    numCode: number;
}

// =============================================================================
// INPUT INTERFACES
// =============================================================================

interface IGetWineInternationalLocationInput {
    total_results: number;
    total_pages: number;
    distributors: IGetWineInternationalDistributorInput[];
}

interface IGetWineInternationalDistributorInput {
    jde_distributor_number: number;
    brand_code: string;
    name: string;
    city: string;
    state: string;
    country: IGetWineInternationalDistributorCountryInput;
    zip: string;
    phone: string;
    url: string;
}

interface IGetWineInternationalDistributorCountryInput {
    iso: string;
    name: string;
    printable_name: string;
    iso3: string;
    numcode: number;
}

/**
 * TODO: Use this function to create the input required to make the API call
 */
const createWineInternationalLocationInput =
    (args: Msdyn365.ICreateActionContext<IGetWineInternationalQueryFilters>): Msdyn365.IActionInput => {
        if (!args.config) {
            throw new Error(`[createWineInternationalLocationInput] configuration was invalid: ${args.config}`);
        }
        return new GetWineInternationalLocationInput(args.config);
    };

/**
 * TODO: Use this function to call your API and process the results as needed
 */
async function action(input: GetWineInternationalLocationInput, ctx: Msdyn365.IActionContext): Promise<IGetWineInternationalLocationOutput> {
    const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;

    const response = await Msdyn365.sendRequest<IGetWineInternationalLocationInput>(input.url, 'get', apiSettings, {
        headers: {
            Authorization: `Token token="${input.token}"`
        }
    });
    return new GetWineLocationInternationalMapper(response.data).output;
}

export default Msdyn365.createDataAction({
    action: <Msdyn365.IAction<IGetWineInternationalLocationOutput>>action,
    input: createWineInternationalLocationInput
});
